import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import "./AnalyticsSection.css";

export default function AnalyticsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("views");

  useEffect(() => {
    loadAnalytics();
  }, []);

  async function loadAnalytics() {
    try {
      const snap = await getDocs(collection(db, "business_analytics"));
      const list = [];

      snap.forEach((doc) => {
        list.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setRows(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const filteredRows = useMemo(() => {
    let list = [...rows];

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();

      list = list.filter((row) => {
        const name = (row.businessName || row.id || "").toLowerCase();
        return name.includes(term);
      });
    }

    if (deviceFilter === "mobile") {
      list = list.filter((row) => (row.mobileViews || 0) > 0);
    }

    if (deviceFilter === "desktop") {
      list = list.filter((row) => (row.desktopViews || 0) > 0);
    }

    if (sortBy === "views") {
      list.sort((a, b) => (b.totalViews || 0) - (a.totalViews || 0));
    } else if (sortBy === "website") {
      list.sort((a, b) => (b.websiteClicks || 0) - (a.websiteClicks || 0));
    } else if (sortBy === "mobile") {
      list.sort((a, b) => (b.mobileViews || 0) - (a.mobileViews || 0));
    } else if (sortBy === "desktop") {
      list.sort((a, b) => (b.desktopViews || 0) - (a.desktopViews || 0));
    }

    return list;
  }, [rows, searchTerm, deviceFilter, sortBy]);

  const oceanPantryClicks = rows
    .filter((row) => (row.businessName || "").trim().replace(/\.$/, "").toLowerCase() === "ocean pantry")
    .reduce((sum, row) => sum + (row.websiteClicks || 0), 0);

  if (loading) {
    return (
      <div className="analytics-section">
        <div className="analytics-card">
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-section">
      <div className="analytics-header">
        <div>
          <h2>Business Analytics</h2>
          <p>View traffic and lead activity for each business page.</p>
        </div>

        <button className="analytics-refresh-btn" onClick={loadAnalytics}>
          Refresh
        </button>
      </div>

      <div className="analytics-controls">
        <input
          className="analytics-search"
          type="text"
          placeholder="Search business..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="analytics-select"
          value={deviceFilter}
          onChange={(e) => setDeviceFilter(e.target.value)}
        >
          <option value="all">All devices</option>
          <option value="mobile">Mobile traffic</option>
          <option value="desktop">Desktop traffic</option>
        </select>

        <select
          className="analytics-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="views">Sort by views</option>
          <option value="website">Sort by website clicks</option>
          <option value="mobile">Sort by mobile views</option>
          <option value="desktop">Sort by desktop views</option>
        </select>
      </div>

      <div className="analytics-summary">
        <div>
          <span>Ocean Pantry — Website Clicks</span>
          <strong>{oceanPantryClicks}</strong>
          <p>Clicks from Ocean Pantry’s listing to its website.</p>
        </div>
        <div>
          <span>Total Businesses</span>
          <strong>{rows.length}</strong>
        </div>
        <div>
          <span>Total Views</span>
          <strong>
            {rows.reduce((sum, row) => sum + (row.totalViews || 0), 0)}
          </strong>
        </div>
        <div>
          <span>Website Clicks</span>
          <strong>
            {rows.reduce((sum, row) => sum + (row.websiteClicks || 0), 0)}
          </strong>
        </div>
      </div>

      <div className="analytics-table-wrap">
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Business</th>
              <th>Views</th>
              <th>Mobile</th>
              <th>Desktop</th>
              <th>Website Clicks</th>
              <th>Phone</th>
              <th>Email</th>
              <th>WhatsApp</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="8" className="analytics-empty">
                  No analytics found.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td className="business-name">
                    {row.businessName || row.id}
                  </td>
                  <td>{row.totalViews || 0}</td>
                  <td>{row.mobileViews || 0}</td>
                  <td>{row.desktopViews || 0}</td>
                  <td>{row.websiteClicks || 0}</td>
                  <td>{row.phoneClicks || 0}</td>
                  <td>{row.emailClicks || 0}</td>
                  <td>{row.whatsappClicks || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
